'use client'

import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Portal,
  Spinner,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { AppField } from '@/shared'
import { CustomSelect } from '@/shared/ui'
import type { Option } from '@/shared/ui/form-builder'
import { useOverrideDeferralForm } from '../model/use-override-deferral-form'
import { FileDropzone } from './FileDropzone'

interface Props {
  open: boolean
  donorId: string
  donorName: string
  /**
   * Overridable deferrals only (`is_overridable`): the value is the journal
   * row's `id` (or the card's `deferral_id`), sent as `deferral_id`.
   */
  deferralOptions: Option[]
  onClose: () => void
}

export function OverrideDeferralModal({
  open,
  donorId,
  donorName,
  deferralOptions,
  onClose,
}: Props) {
  const { t } = useTranslation()

  const { form, isPending, onSubmit } = useOverrideDeferralForm({
    donorId,
    defaultDeferralId:
      deferralOptions.length === 1 ? deferralOptions[0].value : '',
    onSuccess: onClose,
  })

  const {
    register,
    setValue,
    watch,
    reset,
    resetField,
    formState: { errors },
  } = form

  const certificateFile = watch('certificateFile')
  const deferralId = watch('deferralId')

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) handleClose()
      }}
      size="md"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="520px">
            <Dialog.Header
              borderBottomWidth="1px"
              borderColor="border.muted"
              pb={4}
            >
              <Dialog.Title fontSize="lg" fontWeight="bold">
                {t('dashboard.override_deferral.title')}
              </Dialog.Title>
              <Dialog.CloseTrigger
                asChild
                position="absolute"
                top={4}
                right={4}
              >
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={5}>
              <Box mb={4}>
                <Text fontSize="sm" color="fg.muted">
                  {t('dashboard.override_deferral.donor_label')}
                </Text>
                <Text fontSize="sm" fontWeight="semibold">
                  {donorName}
                </Text>
              </Box>

              <form id="override-deferral-form" onSubmit={onSubmit}>
                <VStack gap={4} align="stretch">
                  <AppField.Root invalid={!!errors.deferralId}>
                    <AppField.Label fontSize="sm">
                      {t('dashboard.override_deferral.deferral_label')}
                    </AppField.Label>
                    {deferralOptions.length ? (
                      <CustomSelect
                        width="100%"
                        options={deferralOptions}
                        value={deferralId ? [deferralId] : []}
                        onChange={(value) =>
                          setValue('deferralId', value[0] ?? '', {
                            shouldValidate: true,
                          })
                        }
                        placeholder={t(
                          'dashboard.override_deferral.deferral_placeholder',
                        )}
                      />
                    ) : (
                      <Text fontSize="sm" color="fg.muted">
                        {t('dashboard.override_deferral.no_overridable')}
                      </Text>
                    )}
                    <AppField.ErrorText>
                      {errors.deferralId?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.reasonText}>
                    <AppField.Label fontSize="sm">
                      {t('dashboard.override_deferral.reason_label')}
                    </AppField.Label>
                    <Textarea
                      {...register('reasonText')}
                      rows={4}
                      size="sm"
                      bg="bg.surface"
                      borderColor={errors.reasonText ? undefined : 'border'}
                      borderRadius="md"
                      placeholder={t(
                        'dashboard.override_deferral.reason_placeholder',
                      )}
                    />
                    <AppField.HelperText fontSize="xs" color="fg.muted">
                      {t('dashboard.override_deferral.reason_hint')}
                    </AppField.HelperText>
                    <AppField.ErrorText>
                      {errors.reasonText?.message}
                    </AppField.ErrorText>
                  </AppField.Root>

                  <AppField.Root invalid={!!errors.certificateFile}>
                    <AppField.Label fontSize="sm">
                      {t('dashboard.override_deferral.file_label')}
                    </AppField.Label>
                    <FileDropzone
                      value={certificateFile}
                      invalid={!!errors.certificateFile}
                      onChange={(file) => {
                        if (file) {
                          setValue('certificateFile', file, {
                            shouldValidate: true,
                          })
                        } else {
                          resetField('certificateFile')
                        }
                      }}
                    />
                    <AppField.ErrorText>
                      {errors.certificateFile?.message?.toString()}
                    </AppField.ErrorText>
                  </AppField.Root>
                </VStack>
              </form>
            </Dialog.Body>

            <Dialog.Footer
              borderTopWidth="1px"
              borderColor="border.muted"
              pt={4}
              gap={3}
            >
              <Button
                variant="outline"
                borderRadius="md"
                onClick={handleClose}
                color="fg"
                disabled={isPending}
              >
                {t('dashboard.override_deferral.cancel')}
              </Button>
              <Button
                type="submit"
                form="override-deferral-form"
                bg="primary.solid"
                color="white"
                borderRadius="md"
                px={6}
                _hover={{ opacity: 0.85 }}
                _active={{ opacity: 0.7 }}
                disabled={isPending || !donorId || !deferralId}
              >
                {isPending ? (
                  <Spinner size="xs" color="white" />
                ) : (
                  t('dashboard.override_deferral.submit')
                )}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
